import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { AdminHeading, Card, Field, inputCls } from "@/components/admin/ui";
import { formatRate } from "@/components/home/RatesPreview";

const CATS = ["Paper", "Plastic", "Metal", "E-waste", "Glass", "Other"];
const BLANK = { name: "", category: "Paper", rate_min: "", rate_max: "", unit: "kg", note: "", active: true, sort_order: 0 };

function RateForm({ initial, onSaved, onClose }) {
  const [f, setF] = useState(initial);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = { ...f, rate_min: Number(f.rate_min), rate_max: Number(f.rate_max), sort_order: Number(f.sort_order) || 0 };
    try {
      f.id ? await api.put(`/admin/scrap-rates/${f.id}`, body) : await api.post("/admin/scrap-rates", body);
      toast.success("Rate saved");
      onSaved();
      onClose();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} className="space-y-4" data-testid="rate-form">
      <Field label="Item name"><input required value={f.name} onChange={set("name")} className={inputCls} data-testid="rate-form-name" /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Category">
          <select value={f.category} onChange={set("category")} className={inputCls} data-testid="rate-form-category">{CATS.map((c) => <option key={c}>{c}</option>)}</select>
        </Field>
        <Field label="Unit">
          <select value={f.unit} onChange={set("unit")} className={inputCls} data-testid="rate-form-unit"><option value="kg">per kg</option><option value="piece">per piece</option></select>
        </Field>
        <Field label="Min rate (₹)"><input required type="number" min="0" step="0.5" value={f.rate_min} onChange={set("rate_min")} className={inputCls} data-testid="rate-form-min" /></Field>
        <Field label="Max rate (₹)"><input required type="number" min="0" step="0.5" value={f.rate_max} onChange={set("rate_max")} className={inputCls} data-testid="rate-form-max" /></Field>
      </div>
      <Field label="Note (optional)" hint="e.g. 'Clean & dry', 'Depends on model'"><input value={f.note} onChange={set("note")} maxLength={140} className={inputCls} data-testid="rate-form-note" /></Field>
      <div className="grid grid-cols-2 gap-3 items-end">
        <Field label="Display order"><input type="number" value={f.sort_order} onChange={set("sort_order")} className={inputCls} data-testid="rate-form-order" /></Field>
        <label className="flex items-center gap-3 pb-2.5 text-sm font-medium"><Switch checked={f.active} onCheckedChange={(v) => setF((x) => ({ ...x, active: v }))} data-testid="rate-form-active" /> Show on website</label>
      </div>
      <button disabled={busy} className="btn-leaf w-full" data-testid="rate-form-save">{busy && <Loader2 size={16} className="animate-spin" />} Save rate</button>
    </form>
  );
}

export default function RatesManager() {
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/scrap-rates").then((r) => setRows(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const toggle = async (r) => {
    try { await api.put(`/admin/scrap-rates/${r.id}`, { ...r, active: !r.active }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (r) => {
    if (!window.confirm(`Delete "${r.name}"?`)) return;
    try { await api.delete(`/admin/scrap-rates/${r.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="admin-rates">
      <AdminHeading title="Scrap rates" subtitle="Indicative Jaipur rates shown on the website. Changes go live instantly.">
        <button onClick={() => setEditing(BLANK)} className="btn-leaf !py-2.5 text-sm" data-testid="rate-add-btn"><Plus size={16} /> Add item</button>
      </AdminHeading>
      <Card className="overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-[var(--paper-2)] text-xs uppercase tracking-wider text-[var(--ink-soft)]">
            <tr><th className="text-left px-4 py-3">Item</th><th className="text-left px-4 py-3">Category</th><th className="text-left px-4 py-3">Rate</th><th className="text-left px-4 py-3">Visible</th><th className="px-4 py-3" /></tr>
          </thead>
          <tbody>
            {(rows || []).map((r, i) => (
              <tr key={r.id} className="border-t border-[rgba(20,40,26,.06)]" data-testid={`admin-rate-row-${i}`}>
                <td className="px-4 py-3 font-semibold">{r.name}{r.note && <div className="text-xs font-normal text-[var(--ink-soft)]">{r.note}</div>}</td>
                <td className="px-4 py-3">{r.category}</td>
                <td className="px-4 py-3 font-display font-bold text-[var(--leaf-dark)]">{formatRate(r)} <span className="text-xs font-normal text-[var(--ink-soft)]">/{r.unit}</span></td>
                <td className="px-4 py-3"><Switch checked={r.active} onCheckedChange={() => toggle(r)} data-testid={`admin-rate-toggle-${i}`} /></td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => setEditing(r)} className="p-2 rounded-lg hover:bg-[var(--leaf-soft)]" aria-label="Edit" data-testid={`admin-rate-edit-${i}`}><Pencil size={15} /></button>
                  <button onClick={() => remove(r)} className="p-2 rounded-lg hover:bg-red-50 text-red-600" aria-label="Delete" data-testid={`admin-rate-delete-${i}`}><Trash2 size={15} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows === null && <div className="p-6 text-center"><Loader2 className="animate-spin inline" /></div>}
      </Card>
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg" data-testid="rate-dialog">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit rate" : "Add rate"}</DialogTitle></DialogHeader>
          {editing && <RateForm initial={editing} onSaved={load} onClose={() => setEditing(null)} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
